import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";

export default function Carousel() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const navigate = useNavigate();

  const slides = [
    {
      type: "video",
      url: "https://res.cloudinary.com/ds9vdtt8l/video/upload/v1772841681/vid_2_g9pv6d.mp4",
      title: "L'Excellence",
      titleHighlight: "Automobile",
      subtitle: "Importation directe · Solutions sur mesure"
    },
    {
      type: "image",
      url: "https://res.cloudinary.com/ds9vdtt8l/image/upload/v1772841664/tmg_1_uh3mmc.jpg",
      title: "Performance",
      titleHighlight: "& Style",
      subtitle: "Découvrez notre sélection de sportives"
    },
    {
      type: "image",
      url: "https://res.cloudinary.com/ds9vdtt8l/image/upload/v1772841664/tmg_2_xpu3qe.jpg",
      title: "Confort",
      titleHighlight: "Premium",
      subtitle: "Le luxe accessible pour vos trajets"
    },
    {
      type: "image",
      url: "https://res.cloudinary.com/ds9vdtt8l/image/upload/v1772998273/1771269407281_ic9hce.png",
      title: "Solutions",
      titleHighlight: "Bungalows",
      subtitle: "Espaces modulaires · Qualité certifiée Europe",
      position: "object-[center_25%]" // Remonte l'image pour bien voir la structure du bungalow
    },
    {
      type: "image",
      url: "https://res.cloudinary.com/ds9vdtt8l/image/upload/v1772998186/akboat.nc_e7dc7674e8a049eca23e17fc0c7df2f0_ftufx8.jpg",
      title: "Liberté",
      titleHighlight: "Nautique",
      subtitle: "Large gamme de bateaux neufs · Importation sécurisée",
      position: "object-[center_40%]" // Centre légèrement plus haut pour ne pas couper le haut du bateau
    },
    {
      type: "image",
      url: "https://res.cloudinary.com/ds9vdtt8l/image/upload/v1772841664/tmg_3_x4jj85.jpg",
      title: "Jusqu'à 25%",
      titleHighlight: "d'Économie",
      subtitle: "Sur les tarifs pratiqués localement"
    }
  ];

  const goTo = (idx) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(idx);
    setTimeout(() => setIsTransitioning(false), 700);
  };
  const goNext = () => goTo((currentSlide + 1) % slides.length);
  const goPrev = () => goTo((currentSlide - 1 + slides.length) % slides.length);

  useEffect(() => {
    const isVideo = slides[currentSlide]?.type === "video";
    const timer = setInterval(goNext, isVideo ? 12000 : 6000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlide]);

  return (
    <div className="relative h-[360px] sm:h-[460px] md:h-[580px] lg:h-[640px] w-full overflow-hidden bg-gray-950">
      {slides.map((slide, idx) => (
        <div
          key={idx}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${idx === currentSlide ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
        >
          {/* Média */}
          {slide.type === "video" ? (
            <video
              key={slide.url}
              autoPlay muted loop playsInline
              className="w-full h-full object-cover"
              onLoadedMetadata={(e) => { e.target.muted = true; e.target.play(); }}
            >
              <source src={slide.url} type="video/mp4" />
            </video>
          ) : (
            <img
              src={slide.url}
              alt={slide.title}
              className={`w-full h-full object-cover ${slide.position || 'object-center'} ${
                idx === currentSlide ? 'animate-ken-burns' : ''
              }`}
            />
          )}

          {/* Gradient overlay multi-couche */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />

          {/* Contenu */}
          <div className="absolute inset-0 flex flex-col items-center justify-end pb-16 sm:pb-20 md:pb-24 px-4 sm:px-8 z-10">
            <div className="text-center max-w-4xl">
              {/* Label */}
              <div className="inline-flex items-center gap-2 bg-red-600/90 backdrop-blur text-white text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] px-4 py-1.5 rounded-full mb-4 sm:mb-6">
                <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                Technic Import Outre-Mer
              </div>

              {/* Titre */}
              <h2 className="text-3xl sm:text-5xl md:text-7xl font-black italic uppercase leading-none tracking-tighter text-white drop-shadow-2xl mb-3 sm:mb-4">
                {slide.title}{' '}
                <span className="text-red-500">{slide.titleHighlight}</span>
              </h2>

              {/* Sous-titre */}
              <p className="text-sm sm:text-base md:text-lg text-gray-300 font-medium tracking-wide mb-6 sm:mb-8 uppercase">
                {slide.subtitle}
              </p>

              {/* CTA */}
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/stock'); }}
                className="inline-flex items-center gap-3 bg-red-600 hover:bg-red-700 text-white font-bold py-3.5 px-8 sm:py-4 sm:px-12 rounded-none uppercase tracking-[0.15em] text-xs sm:text-sm transition-all duration-300 shadow-2xl hover:shadow-red-600/40 hover:-translate-y-0.5 active:scale-95 cursor-pointer touch-manipulation relative z-20 group"
              >
                Voir le stock
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Boutons navigation */}
      <button
        onClick={goPrev}
        className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 bg-black/40 hover:bg-red-600 border border-white/20 hover:border-red-600 text-white rounded-full flex items-center justify-center transition-all duration-300 touch-manipulation active:scale-90 backdrop-blur-sm"
        aria-label="Slide précédent"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        onClick={goNext}
        className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 bg-black/40 hover:bg-red-600 border border-white/20 hover:border-red-600 text-white rounded-full flex items-center justify-center transition-all duration-300 touch-manipulation active:scale-90 backdrop-blur-sm"
        aria-label="Slide suivant"
      >
        <ChevronRight size={20} />
      </button>

      {/* Indicateurs (pills) */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goTo(idx)}
            className={`transition-all duration-400 rounded-full touch-manipulation ${idx === currentSlide
                ? 'w-8 sm:w-10 h-2 bg-red-500'
                : 'w-2 h-2 bg-white/40 hover:bg-white/70'
              }`}
            aria-label={`Slide ${idx + 1}`}
          />
        ))}
      </div>

      {/* Numéro en cours */}
      <div className="absolute bottom-5 right-5 text-white/50 text-xs font-mono z-20">
        {String(currentSlide + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
      </div>
    </div>
  );
}
